import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LessonBlockTranslationPatch,
  LessonBlockTranslationProjection,
  LessonBlockTranslationRecord,
} from '@course-authoring/shared-types';
import { fetchWithAuthRetry, parseApiError, type ApiError } from './apiUtils';
import { lessonKeys, withEditedPublishState, type LessonEditorData } from './courseApi';
import {
  invalidateLessonLocaleCoverage,
  lessonDetailsQueryRoot,
} from './lessonLocaleCoverageQueries';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export type LessonBlockTranslationApiError = ApiError;

export const lessonBlockTranslationKeys = {
  all: ['lessonBlockTranslations'] as const,
  detail: (lessonId: string, locale: string, accountId: number | null = null) =>
    [...lessonBlockTranslationKeys.all, lessonId, locale, accountId ?? 'base'] as const,
};

export async function fetchLessonBlockTranslations(
  lessonId: string,
  locale: string,
  accountId: number | null = null,
): Promise<LessonBlockTranslationProjection> {
  const query = new URLSearchParams({ locale });
  if (accountId !== null) query.set('accountId', String(accountId));
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/lessons/${lessonId}/block-translations?${query}`,
  );
  if (!response.ok) throw await parseApiError(response, 'Lesson block translation request failed');
  return response.json();
}

export async function patchLessonBlockTranslation(
  lessonId: string,
  blockId: string,
  locale: string,
  patch: LessonBlockTranslationPatch,
  accountId: number | null = null,
): Promise<LessonBlockTranslationRecord> {
  const query = new URLSearchParams({ locale });
  if (accountId !== null) query.set('accountId', String(accountId));
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/lessons/${lessonId}/block-translations/${blockId}?${query}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );
  if (!response.ok) throw await parseApiError(response, 'Lesson block translation request failed');
  return response.json();
}

export function useLessonBlockTranslationsQuery(
  lessonId: string,
  locale: string,
  accountId: number | null = null,
  enabled = true,
) {
  return useQuery({
    queryKey: lessonBlockTranslationKeys.detail(lessonId, locale, accountId),
    queryFn: () => fetchLessonBlockTranslations(lessonId, locale, accountId),
    enabled: enabled && Boolean(lessonId),
    staleTime: 0,
    retry: (failureCount, error: unknown) =>
      Boolean(error && typeof error === 'object' && 'retryable' in error
        && (error as LessonBlockTranslationApiError).retryable)
      && failureCount < 3,
  });
}

export function useLessonBlockTranslationMutation(
  lessonId: string,
  locale: string,
  accountId: number | null = null,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ blockId, patch }: { blockId: string; patch: LessonBlockTranslationPatch }) =>
      patchLessonBlockTranslation(lessonId, blockId, locale, patch, accountId),
    onSuccess: (record) => {
      queryClient.setQueryData<LessonBlockTranslationProjection>(
        lessonBlockTranslationKeys.detail(lessonId, locale, accountId),
        (current) => current ? {
          ...current,
          blocks: current.blocks.map((block) => block.blockId === record.blockId ? record : block),
        } : current,
      );
      // NA-296: this endpoint flips editedSinceUnlock server-side same as any
      // other save while editing, but (unlike block content/metadata saves)
      // never otherwise touches the lesson detail cache Cancel reads from.
      queryClient.setQueryData<LessonEditorData>(
        lessonKeys.detail(lessonId, 'en', accountId),
        (previous) => previous
          ? { ...previous, publishState: withEditedPublishState(previous) }
          : previous,
      );
      invalidateLessonLocaleCoverage(queryClient, [...lessonDetailsQueryRoot, lessonId]);
    },
  });
}
